import { View } from "@pimo/pimo-app-builder";
import {
  DragAndDropLayout,
  PMBarChartCard,
  PMDonutChartCard,
  PMSmallNumber,
  PMSmallNumberCardEditSchema,
} from "@pimo/pimo-components";
import { APP_CRITICALITIES, APP_RECORD_TYPES, ReportingItem } from "vmd-types";

import { TestAppState } from "../../../app";
import {
  CRITICAL_CRITICALITY_LEVELS,
  CRITICALITY_COLORS_BY_KEY,
  DONUT_CARD_LAYOUT,
  DONUT_CHART_COLORS,
} from "../../../constants";
import {
  countReportingItemsByRecordType,
  filterReportingItemsByRecordType,
  isRecordTypeVisible,
} from "../../../utils/filter-reporting-items-by-record-type";
import { withTotalInTitle } from "../with-total-in-title";

const getCriticalityProgressData = (items: ReportingItem[] | undefined) => {
  const criticalityEntries = Object.entries(APP_CRITICALITIES) as [
    keyof typeof APP_CRITICALITIES,
    (typeof APP_CRITICALITIES)[keyof typeof APP_CRITICALITIES],
  ][];

  const criticalityOrder = criticalityEntries.map(([, label]) => label);

  const countsByCriticality = new Map(
    criticalityOrder.map((criticality) => [criticality, 0])
  );

  for (const item of items ?? []) {
    // Items with no criticality set still count — as "n/a" — so the bars add
    // up to the total item count (#2609).
    const criticality =
      item?.basicItem?.criticality ?? APP_CRITICALITIES.NOT_APPLICABLE;
    if (!countsByCriticality.has(criticality)) {
      continue;
    }
    countsByCriticality.set(
      criticality,
      (countsByCriticality.get(criticality) ?? 0) + 1
    );
  }

  const entries = criticalityEntries.map(([key, criticality]) => {
    const count = countsByCriticality.get(criticality) ?? 0;
    return {
      label: criticality,
      count,
      color: CRITICALITY_COLORS_BY_KEY[key],
    };
  });

  return { entries };
};

/**
 * Adds the critical-DC-systems number, the applications-by-record-type
 * donut, and the DC-systems/applications/(IT-services, when visible)
 * criticality bar charts, in that order (#2222).
 */
export function addCriticalityCards(
  view: View<TestAppState, DragAndDropLayout>
) {
  const criticalDCSystemsComponent = view.addComponent({
    component: PMSmallNumber,
    layoutProps: {
      componentId: "criticalDCSystemsComponent",
      labels: ["#criticalDCSystems"],
      description: "Shows the number of critical DC Business Apps.",
      title: "Critical DC Business Apps",
      editSchema: PMSmallNumberCardEditSchema,
    },
  });

  criticalDCSystemsComponent.mapState(({ currentReport }) => {
    const criticalCount = (currentReport?.project?.reportingItems ?? []).filter(
      (item) =>
        !item?.isChannelHeader &&
        item?.basicItem?.criticality &&
        CRITICAL_CRITICALITY_LEVELS.includes(
          item.basicItem
            .criticality as (typeof CRITICAL_CRITICALITY_LEVELS)[number]
        )
    ).length;

    return {
      title: "Critical DC Business Apps",
      number: criticalCount,
    };
  });

  const applicationsByRecordTypeComponent = view.addComponent({
    component: PMDonutChartCard,
    layoutProps: {
      componentId: "applicationsByRecordTypeComponent",
      labels: ["donutChart", "chart", "recordType"],
      description: "Shows the distribution of tracking items by record type.",
      title: "Applications by Record Type",
    },
  });

  applicationsByRecordTypeComponent.mapState(({ currentReport }) => {
    const reportingItems = currentReport?.project?.reportingItems;

    return {
      title: withTotalInTitle(
        "Applications by Record Type",
        reportingItems?.filter((item) => !item?.isChannelHeader).length ?? 0
      ),
      icon: "DonutLarge",
      ...DONUT_CARD_LAYOUT,
      data: [
        {
          label: "DC Business Apps",
          value: countReportingItemsByRecordType(
            reportingItems,
            APP_RECORD_TYPES.BUSINESS_APPLICATION
          ),
          color: DONUT_CHART_COLORS[0],
        },
        ...(isRecordTypeVisible(APP_RECORD_TYPES.IT_SERVICE)
          ? [
              {
                label: "IT Services",
                value: countReportingItemsByRecordType(
                  reportingItems,
                  APP_RECORD_TYPES.IT_SERVICE
                ),
                color: DONUT_CHART_COLORS[1],
              },
            ]
          : []),
      ],
    };
  });

  const criticalityDCSystemsComponent = view.addComponent({
    component: PMBarChartCard,
    layoutProps: {
      componentId: "criticalityDCSystemsComponent",
      labels: ["barChart", "chart", "criticality", "dcSystems"],
      description: "Shows DC Business Apps split by criticality.",
      title: "Criticality DC Business Apps",
    },
  });

  criticalityDCSystemsComponent.mapState(({ currentReport }) => {
    const allTrackingItems = (
      currentReport?.project?.reportingItems ?? []
    ).filter((item) => !item?.isChannelHeader);
    const { entries } = getCriticalityProgressData(allTrackingItems);

    return {
      title: "Criticality DC Business Apps",
      categories: entries.map((entry) => entry.label),
      data: entries.map((entry) => entry.count),
      chartType: 1 as const,
      xLabel: "Criticality",
      yLabel: "Number of DC Systems",
      colors: entries.map((entry) => entry.color),
      fixedHeight: "320px",
    };
  });

  const criticalityApplicationsComponent = view.addComponent({
    component: PMBarChartCard,
    layoutProps: {
      componentId: "criticalityApplicationsComponent",
      labels: ["barChart", "chart", "criticality", "applications"],
      description: "Shows Applications split by criticality.",
      title: "Criticality Applications",
    },
  });

  criticalityApplicationsComponent.mapState(({ currentReport }) => {
    const applicationItems = filterReportingItemsByRecordType(
      currentReport?.project?.reportingItems,
      APP_RECORD_TYPES.BUSINESS_APPLICATION
    );
    const { entries } = getCriticalityProgressData(applicationItems);

    return {
      title: "Criticality Applications",
      categories: entries.map((entry) => entry.label),
      data: entries.map((entry) => entry.count),
      chartType: 1 as const,
      xLabel: "Criticality",
      yLabel: "Number of Applications",
      colors: entries.map((entry) => entry.color),
      fixedHeight: "320px",
    };
  });

  if (isRecordTypeVisible(APP_RECORD_TYPES.IT_SERVICE)) {
    const criticalityITServicesComponent = view.addComponent({
      component: PMBarChartCard,
      layoutProps: {
        componentId: "criticalityITServicesComponent",
        labels: ["barChart", "chart", "criticality", "itServices"],
        description: "Shows IT Services split by criticality.",
        title: "Criticality IT Services",
      },
    });

    criticalityITServicesComponent.mapState(({ currentReport }) => {
      const serviceItems = filterReportingItemsByRecordType(
        currentReport?.project?.reportingItems,
        APP_RECORD_TYPES.IT_SERVICE
      );
      const { entries } = getCriticalityProgressData(serviceItems);

      return {
        title: "Criticality IT Services",
        categories: entries.map((entry) => entry.label),
        data: entries.map((entry) => entry.count),
        chartType: 1 as const,
        xLabel: "Criticality",
        yLabel: "Number of IT Services",
        colors: entries.map((entry) => entry.color),
        fixedHeight: "320px",
      };
    });
  }
}
